import { TMEstimatorTemplateFeeAPI } from '@business/api/tm_estimator_template_fee';
import { TMEstimatorTemplateFeeId, TMEstimatorTemplateFeeResponse } from '@business/entities/tm/TMEstimatorTemplateFeeResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { TMEstimatorTemplateFeeFormData } from '@modules/settings/SettingsPage/Components/TimeMaterialsTab/AddEditEstimatorTemplateFeeModal/AddEditEstimatorTemplateFeeModal';
import { switchMap, tap } from 'rxjs/operators';

export class TMEstimatorTemplateFeeBloc extends BaseBloc {
  private readonly typeRepo = new Repository<TMEstimatorTemplateFeeResponse>({
    getItemId: item => item.id,
  });

  readonly tmEstimatorTemplateFees$ = this.typeRepo.items$;
  readonly selectTMFee = this.typeRepo.selectItem;

  onReset() {
    this.typeRepo.reset();
  }

  fetchTMEstimatorTemplateFees = () => {
    return TMEstimatorTemplateFeeAPI.fetchTMEstimatorTemplateFees().pipe(
      this.typeRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_estimator_template_fee_error') }),
    );
  };

  fetchTMEstimatorTemplateFee = (id: TMEstimatorTemplateFeeId) => {
    return TMEstimatorTemplateFeeAPI.fetchTMEstimatorTemplateFee(id).pipe(
      this.typeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_estimator_template_fee_error') }),
    );
  };

  createTMEstimatorTemplateFee = (data: TMEstimatorTemplateFeeFormData) => {
    return TMEstimatorTemplateFeeAPI.createTMEstimatorTemplateFee(data).pipe(
      this.typeRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_estimator_template_fee'),
      }),
    );
  };

  // Edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that window saves
  // nothing
  updateTMEstimatorTemplateFee = (id: TMEstimatorTemplateFeeId, data: TMEstimatorTemplateFeeFormData) => {
    return askChangeReason().pipe(
      switchMap(changeNote => TMEstimatorTemplateFeeAPI.updateTMEstimatorTemplateFee(id, data, changeNote)),
      this.typeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_estimator_template_fee'),
      }),
    );
  };

  deleteTMEstimatorTemplateFee = (id: TMEstimatorTemplateFeeId) => {
    return askChangeReason('Delete Estimator Template Fee', 'Do you really want to remove this Estimator Template Fee?', 'Delete').pipe(
      switchMap(changeNote => TMEstimatorTemplateFeeAPI.deleteTMEstimatorTemplateFee(id, changeNote)),
      this.typeRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_estimator_template_fee'),
      }),
    );
  };
}
